"use strict"

function goToPackUrl(url) {
  if (!url) return false;
  if (window.Shopify && window.Shopify.designMode) return false;

  let target;
  try {
    target = new URL(url, window.location.origin);
  } catch (e) {
    return false;
  }

  const current = new URL(window.location.href);
  const samePath = target.pathname.replace(/\/$/, '') === current.pathname.replace(/\/$/, '');
  if (samePath) return false;

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid', 'gclid', 'ttclid', 'msclkid'].forEach((key) => {
    const value = current.searchParams.get(key);
    if (value && !target.searchParams.has(key)) {
      target.searchParams.set(key, value);
    }
  });

  window.location.assign(target.toString());
  return true;
}

class ProductPage {
  constructor(container) {
    if (!container) return;
    if (container.dataset.productPageInitialized === 'true') return;

    this.container = container;
    this.container.dataset.productPageInitialized = 'true';

    this.variantDataElement = this.container.querySelector('[data-product-variants]');
    this.variantData = this.variantDataElement ? JSON.parse(this.variantDataElement.textContent) : [];

    this.currentVariant = this.getInitialVariant();

    this.init();
  }

  init() {
    this.initGallerySwiper();
    this.initPackPicker();
    this.initAjaxAddToCart();
    this.initVideoReviews();
  }

  initVideoReviews() {
    const cards = this.container.querySelectorAll('[data-video-card]');
    if (!cards.length) return;

    const videos = [];

    cards.forEach((card) => {
      if (card.dataset.videoInitialized === 'true') return;
      card.dataset.videoInitialized = 'true';

      const video = card.querySelector('video');
      const playBtn = card.querySelector('[data-video-play]');
      const muteBtn = card.querySelector('[data-video-mute]');
      if (!video) return;

      video.controls = false;
      video.removeAttribute('controls');
      video.muted = false;
      videos.push(video);

      const setPlaying = (playing) => {
        card.classList.toggle('is-playing', playing);
        if (playBtn) {
          playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        }
        if (muteBtn) {
          muteBtn.hidden = !playing;
        }
      };

      const syncMuteUi = () => {
        card.classList.toggle('is-muted', video.muted);
        if (muteBtn) {
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
        }
      };

      const togglePlay = () => {
        if (video.paused) {
          videos.forEach((other) => {
            if (other !== video) other.pause();
          });
          video.muted = false;
          syncMuteUi();
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      };

      if (playBtn) {
        playBtn.addEventListener('click', (event) => {
          event.preventDefault();
          togglePlay();
        });
      }

      video.addEventListener('play', () => setPlaying(true));
      video.addEventListener('pause', () => setPlaying(false));
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        setPlaying(false);
      });

      if (muteBtn) {
        muteBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          syncMuteUi();
        });
      }
    });
  }

  getInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = Number(urlParams.get('variant'));

    if (urlVariantId && this.variantData.length) {
      const matched = this.variantData.find((v) => v.id === urlVariantId);
      if (matched) return matched;
    }

    return this.variantData.find((v) => v.available) || this.variantData[0] || null;
  }

  // ─── SWIPER GALLERY ───────────────────────────────────────────

  initGallerySwiper() {
    if (typeof Swiper === 'undefined') return;

    const mainEl = this.container.querySelector('[data-product-swiper-main]');
    const thumbsEl = this.container.querySelector('[data-product-swiper-thumbs]');

    if (!mainEl) return;

    let thumbsSwiper = null;

    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 8,
        slidesPerView: 7,
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: {
          0: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          1024: { slidesPerView: 7 },
        },
      });
    }

    this.mainSwiper = new Swiper(mainEl, {
      spaceBetween: 0,
      slidesPerView: 1,
      loop: false,
      navigation: {
        prevEl: mainEl.querySelector('[data-swiper-prev]'),
        nextEl: mainEl.querySelector('[data-swiper-next]'),
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
    });

    this.thumbsSwiper = thumbsSwiper;

    if (this.currentVariant && this.currentVariant.featured_media) {
      this.slideToMedia(this.currentVariant.featured_media.id);
    }
  }

  slideToMedia(mediaId) {
    if (!this.mainSwiper) return;

    const slides = this.mainSwiper.slides;
    for (let i = 0; i < slides.length; i++) {
      if (String(slides[i].dataset.mediaId) === String(mediaId)) {
        this.mainSwiper.slideTo(i, 0);
        break;
      }
    }
  }

  // ─── PACK PICKER ──────────────────────────────────────────────

  initPackPicker() {
    const treatmentRoot = this.container.querySelector('[data-treatment-picker]');
    if (treatmentRoot) {
      this.initTreatmentPicker(treatmentRoot);
      return;
    }

    this.packRoot = this.container.querySelector('[data-pack-picker]');
    this.packOptions = this.container.querySelectorAll('[data-pack-option]');
    this.packRadios = this.container.querySelectorAll('[data-pack-radio]');
    this.nailOptions = this.container.querySelectorAll('[data-pack-nail]');
    this.selectedLabel = this.container.querySelector('[data-pack-selected-label]');
    this.selectedPrice = this.container.querySelector('[data-pack-selected-price]');
    this.saveEl = this.container.querySelector('[data-pack-save]');
    this.saveRow = this.container.querySelector('[data-pack-save-row]');
    this.addToCartBtn = this.container.querySelector('[data-pack-add-to-cart]');
    this.buyNowBtn = this.container.querySelector('[data-pack-buy-now]');
    this.btnLabel = this.container.querySelector('[data-pack-btn-label]');

    this.btnPrefix = this.packRoot?.dataset.btnText || 'Choose';
    this.packMode = this.packRoot?.dataset.packMode || 'quantity';
    this.activeOption = null;

    if (!this.packOptions.length) return;

    this.packOptions.forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectPackOption(option, true);
      });
    });

    this.nailOptions.forEach((nail) => {
      nail.addEventListener('click', () => {
        this.selectNail(nail, true);
      });
    });

    if (this.packMode === 'product') {
      const selected = this.container.querySelector('[data-pack-option].pack-picker__option--active')
        || this.packOptions[0];
      this.selectPackOption(selected, false);
      this.highlightNailForPack(selected);
      return;
    }

    const defaultNail = Array.from(this.nailOptions).find((nail) => nail.dataset.default === 'true')
      || this.nailOptions[0];
    if (defaultNail) {
      this.selectNail(defaultNail, false);
    } else {
      const selected = this.container.querySelector('[data-pack-option].pack-picker__option--active')
        || this.packOptions[0];
      this.selectPackOption(selected, false);
    }
  }

  currentPriceCents() {
    if (!this.activeOption) return 0;
    return Number(this.activeOption.dataset.packPrice) || 0;
  }

  updateBuyBtnLabel() {
    if (!this.addToCartBtn || !this.activeOption) return;

    const unitCount = Number(this.activeOption.dataset.unitCount) || this.currentPens || 1;
    const pensWord = unitCount === 1 ? 'Pen' : 'Pens';
    const label = this.btnPrefix + ' ' + unitCount + ' ' + pensWord + ' - ' + this.formatMoney(this.currentPriceCents());

    if (this.btnLabel) {
      this.btnLabel.textContent = label;
    } else {
      this.addToCartBtn.textContent = label;
    }
  }

  updateSummary() {
    if (!this.activeOption) return;

    const unitCount = Number(this.activeOption.dataset.unitCount) || 1;
    const ml = this.activeOption.dataset.ml;
    const salePrice = this.currentPriceCents();
    const saved = Number(this.activeOption.dataset.saved) || 0;
    const pensWord = unitCount === 1 ? 'pen' : 'pens';

    if (this.selectedLabel) {
      this.selectedLabel.textContent = unitCount + ' ' + pensWord + (ml ? ' • ' + ml + 'ml' : '');
    }
    if (this.selectedPrice) {
      this.selectedPrice.textContent = this.formatMoney(salePrice);
    }
    if (this.saveEl) {
      this.saveEl.textContent = this.formatMoney(saved);
    }
    if (this.saveRow) {
      this.saveRow.hidden = saved <= 0;
    }
  }

  syncStickyCart(option) {
    const sticky = document.querySelector('[data-sticky-cart]');
    if (!sticky || !option) return;

    const pens = option.dataset.pens;
    const variantId = option.dataset.variantId;
    sticky.querySelectorAll('[data-sticky-option]').forEach((opt) => {
      const isActive = variantId
        ? opt.dataset.variantId === variantId
        : opt.dataset.pens === pens;
      opt.classList.toggle('sticky-cart__option--active', isActive);
    });

    const priceEl = sticky.querySelector('[data-sticky-price]');
    const addBtn = sticky.querySelector('[data-sticky-add-btn]');
    if (priceEl) {
      priceEl.textContent = option.dataset.priceDisplay || this.formatMoney(Number(option.dataset.packPrice));
    }
    if (addBtn) {
      addBtn.dataset.pens = pens;
      if (variantId) addBtn.dataset.variantId = variantId;
      if (option.dataset.quantity) addBtn.dataset.quantity = option.dataset.quantity;
    }
  }

  selectPackOption(option, navigate) {
    if (!option) return;

    if (navigate && this.packMode === 'product' && goToPackUrl(option.dataset.productUrl)) {
      return;
    }

    const pens = Number(option.dataset.pens) || 1;
    this.currentPens = pens;
    this.activeOption = option;

    this.packOptions.forEach((opt) => {
      const isActive = opt === option;
      opt.classList.toggle('pack-picker__option--active', isActive);
      const radio = opt.querySelector('[data-pack-radio]');
      if (radio) radio.checked = isActive;
    });

    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.pens = pens;
      this.addToCartBtn.dataset.quantity = option.dataset.quantity || String(pens);
      if (option.dataset.variantId) {
        this.addToCartBtn.dataset.variantId = option.dataset.variantId;
      }
    }
    if (this.buyNowBtn) {
      const variantId = option.dataset.variantId || (this.addToCartBtn ? this.addToCartBtn.dataset.variantId : '');
      const quantity = Number(option.dataset.quantity) || pens;
      this.buyNowBtn.href = '/cart/' + variantId + ':' + quantity + '?checkout';
    }

    this.updateSummary();
    this.updateBuyBtnLabel();
    this.syncStickyCart(option);
  }

  highlightNail(nail) {
    this.nailOptions.forEach((option) => {
      const isActive = option === nail;
      option.classList.toggle('pack-picker__nail--active', isActive);
      option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  highlightNailForPack(option) {
    if (!option) return;
    const pens = Number(option.dataset.pens) || 0;
    const match = Array.from(this.nailOptions).find((nail) => Number(nail.dataset.recommendedPens) === pens);
    if (match) this.highlightNail(match);
  }

  selectNail(nail, navigate) {
    if (!nail) return;

    this.highlightNail(nail);

    const recommendedPens = Number(nail.dataset.recommendedPens) || 0;
    let match = null;
    this.packOptions.forEach((option) => {
      if (Number(option.dataset.pens) === recommendedPens) match = option;
    });

    const next = match || this.activeOption || this.packOptions[0];
    this.selectPackOption(next, navigate);
  }

  initTreatmentPicker(root) {
    this.packRoot = root;
    this.addToCartBtn = root.querySelector('[data-pack-add-to-cart]');
    this.btnLabel = root.querySelector('[data-pack-btn-label]');
    this.btnPrefix = root.dataset.addText || 'Add to cart';

    let variants = [];
    const variantsEl = root.querySelector('[data-treatment-packs]')
      || root.querySelector('[data-treatment-variants]');
    try {
      variants = JSON.parse(variantsEl?.textContent || '[]') || [];
    } catch (e) {
      variants = [];
    }

    const nailsStep = root.querySelector('[data-treatment-step="nails"]');
    const coursesStep = root.querySelector('[data-treatment-step="courses"]');
    const countEl = root.querySelector('[data-nails-count]');
    const minusBtn = root.querySelector('[data-nails-minus]');
    const plusBtn = root.querySelector('[data-nails-plus]');
    const calculateBtn = root.querySelector('[data-treatment-calculate]');
    const editBtn = root.querySelector('[data-treatment-edit]');
    const summaryTitle = root.querySelector('[data-treatment-summary-title]');
    const summaryMeta = root.querySelector('[data-treatment-summary-meta]');
    const courseCards = root.querySelectorAll('[data-treatment-course]');
    const calculatedTemplate = root.dataset.calculatedTemplate || 'Supply calculated for {count} affected nails';

    let rules = [];
    const rulesEl = root.querySelector('[data-treatment-rules]');
    try {
      rules = JSON.parse(rulesEl?.textContent || '[]') || [];
    } catch (e) {
      rules = [];
    }
    if (!rules.length) {
      rules = [
        { from: 1, to: 1, first: 30, full: 60 },
        { from: 2, to: 3, first: 60, full: 90 },
        { from: 4, to: 0, first: 90, full: 120 },
      ];
    }

    let nails = 1;
    let selectedKey = 'full';
    const minNails = Math.min(...rules.map((rule) => Number(rule.from) || 1), 1);
    let maxNails = 1;
    rules.forEach((rule) => {
      const from = Number(rule.from) || 1;
      const to = Number(rule.to) || 0;
      maxNails = Math.max(maxNails, to > 0 ? to : from);
    });
    const maxOverride = Number(root.dataset.maxNails);
    if (maxOverride > 0) maxNails = Math.max(minNails, maxOverride);
    nails = minNails;

    const ruleFor = (count) => rules.find((rule) => {
      const from = Number(rule.from) || 1;
      const to = Number(rule.to) || 0;
      return count >= from && (to === 0 || count <= to);
    }) || null;

    const variantByDays = (days) => variants.find((item) => Number(item.days) === Number(days) && item.available !== false)
      || variants.find((item) => Number(item.days) === Number(days));

    const baseline = [...variants]
      .filter((item) => Number(item.days) > 0 && Number(item.pens) > 0)
      .sort((a, b) => Number(a.days) - Number(b.days))[0];

    const saveFor = (variant) => {
      if (!variant) return 0;
      const compare = Number(variant.compare_at_price) || 0;
      if (compare > Number(variant.price)) return compare - Number(variant.price);
      if (!baseline || !baseline.pens || Number(variant.days) <= Number(baseline.days)) return 0;
      const unit = Number(baseline.price) / Number(baseline.pens);
      const expected = unit * Number(variant.pens);
      const save = Math.round(expected - Number(variant.price));
      return save > 0 ? save : 0;
    };

    const fillCard = (card, variant) => {
      if (!card) return;
      const missing = !variant;
      card.hidden = missing;
      card.disabled = missing;
      if (missing) return;

      const days = Number(variant.days) || 0;
      const pens = Number(variant.pens) || 0;
      const price = Number(variant.price) || 0;
      const save = saveFor(variant);
      const perDay = days > 0 ? price / days : 0;

      const titleEl = card.querySelector('[data-course-title]');
      const pensEl = card.querySelector('[data-course-pens]');
      const priceEl = card.querySelector('[data-course-price]');
      const dayEl = card.querySelector('[data-course-day]');
      const saveEl = card.querySelector('[data-course-save]');

      if (titleEl) titleEl.textContent = days + '-Day Supply';
      if (pensEl) pensEl.textContent = pens + (pens === 1 ? ' Pen' : ' Pens');
      if (priceEl) priceEl.textContent = this.formatMoney(price);
      if (dayEl) dayEl.textContent = days ? 'Just ' + this.formatMoney(perDay) + ' / day' : '';
      if (saveEl) {
        saveEl.hidden = save <= 0;
        saveEl.textContent = save > 0 ? 'Save ' + this.formatMoney(save) : '';
      }
      card.dataset.variantId = String(variant.id);
      card.dataset.price = String(price);
    };

    const selectedVariant = () => {
      const card = root.querySelector('[data-treatment-course].treatment-picker__course--active');
      const id = Number(card?.dataset.variantId);
      return variants.find((item) => Number(item.id) === id) || null;
    };

    const syncSelection = () => {
      const variant = selectedVariant();
      const price = Number(variant?.price) || 0;
      const canAdd = Boolean(variant) && variant.available !== false;
      if (this.addToCartBtn) {
        this.addToCartBtn.dataset.variantId = variant ? String(variant.id) : '';
        this.addToCartBtn.dataset.pens = '1';
        this.addToCartBtn.dataset.quantity = '1';
        this.addToCartBtn.disabled = !canAdd;
      }
      if (this.btnLabel) {
        if (!variant) {
          this.btnLabel.textContent = this.btnPrefix;
        } else if (!canAdd) {
          this.btnLabel.textContent = 'Sold out';
        } else {
          this.btnLabel.textContent = this.btnPrefix + ' — ' + this.formatMoney(price);
        }
        this.treatmentBtnLabel = this.btnLabel.textContent;
      }
      const sticky = document.querySelector('[data-sticky-cart]');
      if (sticky) {
        const priceEl = sticky.querySelector('[data-sticky-price]');
        const addBtn = sticky.querySelector('[data-sticky-add-btn]');
        if (priceEl && variant) priceEl.textContent = this.formatMoney(price);
        if (addBtn && variant) {
          addBtn.dataset.variantId = String(variant.id);
          addBtn.dataset.pens = '1';
          addBtn.dataset.quantity = '1';
          addBtn.disabled = !canAdd;
        }
      }
    };

    const renderCourses = () => {
      const map = ruleFor(nails) || {};
      fillCard(
        root.querySelector('[data-treatment-course="first"]'),
        Number(map.first) > 0 ? variantByDays(map.first) : null
      );
      fillCard(
        root.querySelector('[data-treatment-course="full"]'),
        Number(map.full) > 0 ? variantByDays(map.full) : null
      );

      const firstCard = root.querySelector('[data-treatment-course="first"]');
      const fullCard = root.querySelector('[data-treatment-course="full"]');
      const preferred = selectedKey === 'first' ? firstCard : fullCard;
      const fallback = !preferred?.hidden ? preferred : (!fullCard?.hidden ? fullCard : firstCard);
      courseCards.forEach((card) => {
        const isActive = card === fallback && !card.hidden;
        card.classList.toggle('treatment-picker__course--active', isActive);
        card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (fallback?.hidden) selectedKey = fallback?.dataset.treatmentCourse || 'full';
      else selectedKey = fallback?.dataset.treatmentCourse || 'full';
      syncSelection();
    };

    const updateCount = () => {
      if (countEl) countEl.textContent = String(nails);
      if (minusBtn) minusBtn.disabled = nails <= minNails;
      if (plusBtn) plusBtn.disabled = nails >= maxNails;
    };

    const showCourses = () => {
      const label = nails === 1 ? '1 nail' : nails + ' nails';
      if (summaryTitle) {
        summaryTitle.textContent = calculatedTemplate.replace('{count}', String(nails));
      }
      if (summaryMeta) summaryMeta.textContent = label;
      renderCourses();
      if (nailsStep) nailsStep.hidden = true;
      if (coursesStep) coursesStep.hidden = false;
    };

    const showNails = () => {
      if (coursesStep) coursesStep.hidden = true;
      if (nailsStep) nailsStep.hidden = false;
    };

    minusBtn?.addEventListener('click', () => {
      if (nails > minNails) nails -= 1;
      updateCount();
    });
    plusBtn?.addEventListener('click', () => {
      if (nails < maxNails) nails += 1;
      updateCount();
    });
    calculateBtn?.addEventListener('click', showCourses);
    editBtn?.addEventListener('click', showNails);

    courseCards.forEach((card) => {
      card.addEventListener('click', () => {
        if (card.hidden) return;
        selectedKey = card.dataset.treatmentCourse || 'full';
        courseCards.forEach((item) => {
          const isActive = item === card;
          item.classList.toggle('treatment-picker__course--active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        syncSelection();
      });
    });

    updateCount();
  }

  // ─── ADD TO CART ──────────────────────────────────────────────

  initAjaxAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async () => {
      const isTreatment = Boolean(this.container.querySelector('[data-treatment-picker]'));
      const variantId = Number(this.activeOption?.dataset.variantId || this.addToCartBtn.dataset.variantId);
      const quantity = isTreatment
        ? 1
        : (Number(this.activeOption?.dataset.quantity || this.addToCartBtn.dataset.quantity) || 1);
      if (!variantId) return;

      try {
        this.addToCartBtn.disabled = true;
        if (this.btnLabel) {
          this.btnLabel.textContent = 'Adding...';
        } else {
          this.addToCartBtn.textContent = 'Adding...';
        }
        const properties = this.packMode === 'product' ? { _pack_mode: 'product' } : {};
        await addCartItem(variantId, quantity, properties);
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        console.error('Pack picker add to cart error:', error);
      } finally {
        this.addToCartBtn.disabled = false;
        if (this.treatmentBtnLabel && this.btnLabel) {
          this.btnLabel.textContent = this.treatmentBtnLabel;
        } else {
          this.updateBuyBtnLabel();
        }
      }
    });
  }

  // ─── HELPERS ──────────────────────────────────────────────────

  formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format((cents || 0) / 100);
  }
}

const initProductPages = (scope = document) => {
  const productSections = scope.querySelectorAll('[data-product-page]');
  productSections.forEach((section) => {
    new ProductPage(section);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initProductPages();
  initStickyCart();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductPages(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  initProductPages();
});



// STICKY CART (mobile only)

function initStickyCart() {
  const stickyCart = document.querySelector('[data-sticky-cart]');
  if (!stickyCart) return;

  const packPicker = document.querySelector('[data-pack-picker]');
  const treatmentPicker = document.querySelector('[data-treatment-picker]');
  const buyNow = document.querySelector('[data-buy-now]');
  const trigger = packPicker || treatmentPicker || buyNow;
  if (!trigger) return;

  const addBtn = stickyCart.querySelector('[data-sticky-add-btn]');

  function isFooterVisible() {
    const footer = document.querySelector('footer') || document.querySelector('[data-section-type="footer"]');
    if (!footer) return false;
    return footer.getBoundingClientRect().top < window.innerHeight;
  }

  function updateVisibility() {
    if (window.innerWidth > 768 || document.body.classList.contains('sticky-buy-modal-open')) {
      stickyCart.classList.remove('is-visible');
      stickyCart.setAttribute('aria-hidden', 'true');
      return;
    }

    const isLanding = Boolean(buyNow) && !packPicker;
    let shouldShow = false;

    if (isLanding) {
      const pastFirstScreen = window.scrollY > window.innerHeight * 0.45;
      shouldShow = pastFirstScreen && !isFooterVisible();
    } else {
      const pickerRect = trigger.getBoundingClientRect();
      const pickerHeight = pickerRect.height;
      const visiblePortion = Math.min(Math.max(pickerRect.bottom, 0), pickerHeight);
      const visibleRatio = pickerHeight > 0 ? visiblePortion / pickerHeight : 1;
      shouldShow = visibleRatio < 0.45 && !isFooterVisible();
    }

    if (shouldShow) {
      stickyCart.classList.add('is-visible');
      stickyCart.setAttribute('aria-hidden', 'false');
    } else {
      stickyCart.classList.remove('is-visible');
      stickyCart.setAttribute('aria-hidden', 'true');
    }
  }

  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility, { passive: true });
  updateVisibility();

  const modal = document.querySelector('[data-sticky-modal]');
  if (treatmentPicker && addBtn) {
    addBtn.addEventListener('click', async function () {
      const variantId = Number(addBtn.dataset.variantId);
      if (!variantId) return;
      addBtn.disabled = true;
      try {
        await addCartItem(variantId, 1);
        refreshCartUi({ openDrawer: true });
      } catch (err) {
        console.error('Treatment sticky add to cart error:', err);
      } finally {
        addBtn.disabled = false;
      }
    });
    return;
  }

  if (modal) {
    initStickyBuyModal(stickyCart, modal, addBtn);
  }
}

function formatStickyMoney(cents, currency) {
  const code = currency || document.querySelector('[data-sticky-modal]')?.dataset.currency || 'GBP';
  const locale = code === 'USD' ? 'en-US' : 'en-GB';
  return (cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
  });
}

function initStickyBuyModal(stickyCart, modal, openBtn) {
  const options = modal.querySelectorAll('[data-sticky-modal-option]');
  const addBtn = modal.querySelector('[data-sticky-modal-add]');
  const priceEl = stickyCart.querySelector('[data-sticky-price]');
  const variantId = Number(modal.dataset.variantId);
  const currency = modal.dataset.currency || 'GBP';
  let selectedOption = modal.querySelector('[data-sticky-modal-option].sticky-cart__option--active') || options[0];

  function money(cents) {
    return formatStickyMoney(cents, currency);
  }

  function currentPackPrice() {
    return Number(selectedOption?.dataset.packPrice) || 0;
  }

  function updatePrices() {
    const packPrice = currentPackPrice();

    if (addBtn) {
      addBtn.textContent = 'Add to cart - ' + money(packPrice);
    }
    if (priceEl && selectedOption) {
      priceEl.textContent = selectedOption.dataset.priceDisplay || money(packPrice);
    }
  }

  function selectOption(option) {
    if (!option) return;
    selectedOption = option;
    options.forEach(function (opt) {
      const isActive = opt === option;
      opt.classList.toggle('sticky-cart__option--active', isActive);
      opt.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    if (openBtn) {
      openBtn.dataset.pens = option.dataset.pens;
      if (option.dataset.variantId) openBtn.dataset.variantId = option.dataset.variantId;
      if (option.dataset.quantity) openBtn.dataset.quantity = option.dataset.quantity;
    }
    updatePrices();
  }

  function syncFromPage() {
    const activePack = document.querySelector(
      '[data-pack-option].pack-picker__option--active, [data-buy-option].buy-now__option--active'
    );
    const activeVariant = activePack && activePack.dataset.variantId;
    const pens = (activePack && activePack.dataset.pens) || openBtn?.dataset.pens;
    let match = null;
    options.forEach(function (opt) {
      if (activeVariant && opt.dataset.variantId === activeVariant) match = opt;
      else if (!activeVariant && pens && opt.dataset.pens === pens) match = opt;
    });
    selectOption(match || selectedOption || options[0]);
  }

  function openModal() {
    syncFromPage();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sticky-buy-modal-open');
    stickyCart.classList.remove('is-visible');
    stickyCart.setAttribute('aria-hidden', 'true');
  }

  function closeModal() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sticky-buy-modal-open');
    window.dispatchEvent(new Event('scroll'));
  }

  if (openBtn) {
    openBtn.addEventListener('click', function () {
      openModal();
    });
  }

  modal.querySelectorAll('[data-sticky-modal-close]').forEach(function (el) {
    el.addEventListener('click', closeModal);
  });

  options.forEach(function (option) {
    option.addEventListener('click', function () {
      selectOption(option);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) {
      closeModal();
    }
  });

  window.addEventListener('resize', function () {
    if (window.innerWidth > 768) closeModal();
  }, { passive: true });

  if (addBtn) {
    addBtn.addEventListener('click', async function () {
      const lineVariant = Number(selectedOption?.dataset.variantId) || variantId;
      if (!lineVariant || !selectedOption) return;

      addBtn.disabled = true;
      addBtn.textContent = 'Redirecting...';

      try {
        const quantity = Number(selectedOption.dataset.quantity) || Number(selectedOption.dataset.pens) || 1;
        const properties = selectedOption.dataset.packMode === 'product' ? { _pack_mode: 'product' } : {};
        await addCartItem(lineVariant, quantity, properties);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('Sticky modal checkout error:', err);
        addBtn.disabled = false;
        updatePrices();
      }
    });
  }

  updatePrices();
}

function formatMoneyGBP(cents) {
  return formatStickyMoney(cents, 'GBP');
}

// UPSELL

function parseUpsellVariantIds(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter(Boolean);
}

function glowBundledCartSectionPayload() {
  const ids = [];
  const drawer = document.querySelector('[data-cart-drawer-root]');
  if (drawer?.dataset.sectionId) {
    ids.push(drawer.dataset.sectionId);
  }

  const cartRoot = document.querySelector('[data-main-cart-root]');
  if (cartRoot?.dataset.sectionId) {
    ids.push(cartRoot.dataset.sectionId);
  }

  if (!ids.length) {
    return null;
  }

  const cartPath = window.__glowRoutes?.cartUrl || '/cart';
  const sections_url = cartPath.startsWith('/') ? cartPath : `/${cartPath}`;

  return {
    sections: ids.join(','),
    sections_url,
  };
}

function glowApplyBundledCartSections(result) {
  if (result?.sections && window.cartUi?.applyBundledSections) {
    window.cartUi.applyBundledSections(result.sections);
  }
}

async function getCartData() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load cart');
  }

  return response.json();
}

async function addCartItem(variantId, quantity = 1, properties = {}, sellingPlanId = null) {
  const payload = {
    id: Number(variantId),
    quantity,
    properties,
    ...(glowBundledCartSectionPayload() || {}),
  };

  if (sellingPlanId) {
    payload.selling_plan = Number(sellingPlanId);
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to add item to cart';
    try {
      const errorData = await response.json();
      message = errorData.description || errorData.message || message;
    } catch (e) {
      // keep default message
    }
    throw new Error(message);
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function updateCartItemQuantity(lineKey, quantity, sellingPlanId = null) {
  const payload = {
    id: lineKey,
    quantity,
    ...(glowBundledCartSectionPayload() || {}),
  };

  if (sellingPlanId && quantity > 0) {
    payload.selling_plan = Number(sellingPlanId);
  }

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to update cart item quantity');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

window.updateCartItemQuantity = updateCartItemQuantity;

async function addUpsellGiftIfNeeded(section) {
  if (!section) return false;

  const productsWrap = section.querySelector('.product-upsell__products');
  if (!productsWrap) return false;

  const requiredVariantIds = parseUpsellVariantIds(productsWrap.dataset.requiredVariants);
  const giftVariantId = Number(section.dataset.giftVariantId);

  if (!requiredVariantIds.length || !giftVariantId) {
    return false;
  }

  const cart = await getCartData();
  const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

  const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
    cartVariantIds.includes(variantId)
  );

  const hasGiftAlready = cartVariantIds.includes(giftVariantId);

  if (!hasAllRequiredProducts || hasGiftAlready) {
    return false;
  }

  await addCartItem(giftVariantId, 1, {
    upsell_gift: 'true',
    upsell_required_variant_ids: requiredVariantIds.join(','),
  });

  return true;
}

function refreshCartUi(detail = {}) {
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
}

async function removeInvalidUpsellGiftsIfNeeded() {
  const cart = await getCartData();
  const cartVariantIds = new Set(cart.items.map((item) => Number(item.variant_id)));
  const giftLineKeysToRemove = [];

  cart.items.forEach((item) => {
    const properties = item.properties || {};
    const isUpsellGift =
      String(properties.upsell_gift).toLowerCase() === 'true'
      || String(properties._upsell_gift).toLowerCase() === 'true';
    if (!isUpsellGift) return;

    const requiredVariantIds = parseUpsellVariantIds(
      properties.upsell_required_variant_ids || properties._upsell_required_variant_ids
    );
    if (!requiredVariantIds.length) return;

    const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
      cartVariantIds.has(variantId)
    );

    if (!hasAllRequiredProducts) {
      giftLineKeysToRemove.push(item.key);
    }
  });

  if (!giftLineKeysToRemove.length) return false;

  await Promise.all(giftLineKeysToRemove.map((lineKey) => updateCartItemQuantity(lineKey, 0)));
  return true;
}

let isUpsellGiftSyncInProgress = false;

async function syncUpsellGiftState() {
  if (isUpsellGiftSyncInProgress) return;

  isUpsellGiftSyncInProgress = true;
  try {
    const wasGiftRemoved = await removeInvalidUpsellGiftsIfNeeded();
    if (wasGiftRemoved) {
      refreshCartUi();
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    }
  } catch (error) {
    console.error('Upsell gift sync error:', error);
  } finally {
    isUpsellGiftSyncInProgress = false;
  }
}

function setAjaxButtonLoadingState(button, loading, loadingText = 'Adding to cart...') {
  if (!button) return;

  if (!button.dataset.initialText) {
    button.dataset.initialText = button.textContent.trim();
  }

  if (loading) {
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = loadingText;
  } else {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
    button.textContent = button.dataset.initialText;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('.product-upsell .product-upsell__card-button');
  if (!button) return;

  event.preventDefault();

  const section = button.closest('.product-upsell');
  const card = button.closest('.product-upsell__card');
  const variantId = Number(button.dataset.variantId || card?.dataset.variantId);

  if (!section || !variantId) return;

  try {
    setAjaxButtonLoadingState(button, true);

    await addCartItem(variantId);

    await addUpsellGiftIfNeeded(section);

    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Upsell add to cart error:', error);
  } finally {
    setAjaxButtonLoadingState(button, false);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ajax-add-to-cart-form]');
  if (!form) return;

  event.preventDefault();

  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const submitButton = form.querySelector('[data-ajax-add-to-cart-button]');
  const variantId = Number(variantInput?.value);
  const quantity = Number(quantityInput?.value || 1);

  if (!variantId) return;

  try {
    setAjaxButtonLoadingState(submitButton, true);
    await addCartItem(variantId, quantity > 0 ? quantity : 1);
    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Add to cart form error:', error);
  } finally {
    setAjaxButtonLoadingState(submitButton, false);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  syncUpsellGiftState();
});

document.addEventListener('cart:refresh', () => {
  syncUpsellGiftState();
});


// ACCORDION

function initProductAccordion(scope = document) {
  const accordions = scope.querySelectorAll('[data-product-accordion]');

  accordions.forEach((accordion) => {
    if (accordion.dataset.accordionInitialized === 'true') return;
    accordion.dataset.accordionInitialized = 'true';

    const items = accordion.querySelectorAll('.product-accordion__item');

    let hasOpenedItem = false;

    items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');

      if (!trigger || !panel) return;

      panel.hidden = false;
      panel.style.overflow = 'hidden';

      if (item.classList.contains('is-open') && !hasOpenedItem) {
        hasOpenedItem = true;
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = 'auto';
      } else {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = '0px';
      }
    });

    accordion.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const item = trigger.closest('.product-accordion__item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');

      items.forEach((accordionItem) => {
        if (accordionItem !== item && accordionItem.classList.contains('is-open')) {
          closeAccordionItem(accordionItem);
        }
      });

      if (isOpen) {
        closeAccordionItem(item);
      } else {
        openAccordionItem(item);
      }
    });
  });
}

function openAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  item.classList.add('is-open');
  trigger.setAttribute('aria-expanded', 'true');

  panel.style.height = `${panel.scrollHeight}px`;

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'height') return;

    if (item.classList.contains('is-open')) {
      panel.style.height = 'auto';
    }

    panel.removeEventListener('transitionend', onTransitionEnd);
  };

  panel.addEventListener('transitionend', onTransitionEnd);
}

function closeAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  panel.style.height = `${panel.scrollHeight}px`;
  panel.offsetHeight;

  item.classList.remove('is-open');
  trigger.setAttribute('aria-expanded', 'false');

  requestAnimationFrame(() => {
    panel.style.height = '0px';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductAccordion();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductAccordion(event.target);
});